'use client'

import React from 'react'
import { useState, useEffect } from 'react'
import type { ImageProps } from 'next/image'
import Image from 'next/image'
import { HiLockClosed } from 'react-icons/hi'
import { FaCaretDown, FaCaretRight } from 'react-icons/fa'
import { Locale } from '@marketing-sites/utils'
import bgImage from '../assets/re-header-bg.webp'
import reLogo from '../assets/re-logo-white-shadow.svg'
import reLogoAbc from '../assets/re-logo-abc-white-shadow.svg'
import reggie from '../assets/reggie-welcome.webp'
import { ReadingEggsNavBar } from './ReadingEggsNavBar'
import { Container } from './Container'

function LoginButton() {
  return (
    <a
      href="https://app.readingeggs.com/login?_ga=2.93262621.1032280199.1647816056-1872540984.1639361619"
      className="flex h-[35px] items-center space-x-1 rounded border border-[#168fbc] bg-[#50cfff] bg-gradient-to-b from-[#50cfff] via-[#86d4f1] via-5% to-[#20afe5] px-3 pl-2 font-bold uppercase text-white [text-shadow:0_1px_1px_rgb(0_0_0_/_40%)] hover:bg-[#0299ca] hover:bg-none hover:text-white hover:no-underline focus:bg-[#0299ca] focus:bg-none focus:text-white focus:no-underline"
    >
      <HiLockClosed size={15} />
      <span>Login</span>
    </a>
  )
}

function SignupButton() {
  return (
    <a
      href="/signup/"
      className="flex h-[35px] items-center space-x-1 rounded border border-[#91580f] bg-[#df8506] bg-gradient-to-b from-[#fff130] via-[#ffe616] via-5% to-[#f99f1f] px-3 pr-2 font-bold uppercase text-white [text-shadow:0_1px_1px_rgb(0_0_0_/_40%)] hover:bg-[#df8506] hover:bg-none hover:text-white hover:no-underline focus:bg-[#df8506] focus:bg-none focus:text-white focus:no-underline"
    >
      <span>Sign Up</span>
      <FaCaretRight size={15} />
    </a>
  )
}

interface ProductLinkProps {
  href?: string
  children: string
  isActive?: boolean
}

function ProductLink({ href, children, isActive }: ProductLinkProps) {
  const baseStyle =
    'px-2 text-white uppercase text-[10px] inline-block leading-[38px] tracking-widest hover:text-white focus:text-white'
  const inactive = `${baseStyle} hover:underline`
  const activeStyle = `${baseStyle} shadow-inner-xl`
  return (
    <li className="h-full">
      <a href={href} className={isActive ? activeStyle : inactive}>
        {children}
      </a>
    </li>
  )
}

interface ParentSchoolToggleLinkProps {
  id?: string
  children: string
  href?: string
  submenuItems?: { linkUrl: string; text: string }[]
  submenuOpen?: string
  setSubmenuOpen?: (value: React.SetStateAction<string>) => void
}

function ParentSchoolToggleLink({
  id,
  children,
  href,
  submenuItems,
  submenuOpen,
  setSubmenuOpen,
}: ParentSchoolToggleLinkProps) {
  function handleClick(e: React.MouseEvent) {
    if (id && submenuItems && setSubmenuOpen) {
      e.preventDefault()
      setSubmenuOpen((s) => (s == id ? '' : id))
    }
  }

  return (
    <li className="relative cursor-pointer select-none" data-click-override>
      <a
        onClick={handleClick}
        href={href}
        className="flex h-[30px] rounded border border-[#0384b4] bg-[#0696cd] px-3 text-[13px] leading-[28px] text-white hover:text-white focus:text-white"
      >
        {children}
        {submenuItems && <FaCaretDown className="ml-1 mt-1 self-center" />}
      </a>
      {submenuItems && submenuOpen == id && (
        <div className="absolute mt-1.5 rounded border border-[#0384b4] bg-[#0696cd] text-sm text-white">
          {submenuItems.map((item) => (
            <a
              key={item.linkUrl}
              href={item.linkUrl}
              className="block whitespace-nowrap rounded px-3 py-1 hover:bg-[#22AFE5]"
            >
              {item.text}
            </a>
          ))}
        </div>
      )}
    </li>
  )
}

interface ReadingEggsHeaderProps {
  locale: Locale
}

export function ReadingEggsHeader({ locale }: ReadingEggsHeaderProps) {
  const siteUrls = siteUrlsForLocale(locale)
  let logo = reLogo as ImageProps['src']
  let logoAltText = 'Reading Eggs logo'
  if (locale === Locale.au) {
    logo = reLogoAbc as ImageProps['src']
    logoAltText = `ABC ${logoAltText}`
  }

  const [submenuOpen, setSubmenuOpen] = useState('')

  useEffect(() => {
    //close submenu if clicking outside
    function callback(e: MouseEvent) {
      const target = e.target as HTMLElement
      if (target.closest('[data-click-override]') === null) {
        setSubmenuOpen('')
      }
    }

    document.body.addEventListener('click', callback)
    return () => {
      document.body.removeEventListener('click', callback)
    }
  }, [])

  return (
    <header className="">
      <div className="fixed z-30 h-[38px] w-screen border-b border-[#168fbc] bg-[#20afe5]">
        <Container className="flex h-full justify-between">
          <>
            <div className="flex h-full">
              <nav
                role="navigation"
                aria-label="Audience"
                className="mr-5 hidden h-full md:flex"
              >
                <ul className="flex items-center space-x-1">
                  <ParentSchoolToggleLink href="/">
                    Parents
                  </ParentSchoolToggleLink>
                  {[Locale.ca, Locale.us].includes(locale) ? (
                    <ParentSchoolToggleLink
                      id="schools"
                      submenuOpen={submenuOpen}
                      setSubmenuOpen={setSubmenuOpen}
                      submenuItems={[
                        { linkUrl: '/schools/admin/', text: 'Administrators' },
                        { linkUrl: '/schools/', text: 'Teachers' },
                      ]}
                    >
                      Schools
                    </ParentSchoolToggleLink>
                  ) : (
                    <ParentSchoolToggleLink href="/schools/">
                      Schools
                    </ParentSchoolToggleLink>
                  )}
                </ul>
              </nav>

              <nav
                role="navigation"
                aria-label="Product"
                className="hidden h-full lg:flex"
              >
                <ul className="flex items-center">
                  <ProductLink href={siteUrls.re} isActive={true}>
                    Reading Eggs
                  </ProductLink>
                  <ProductLink href={siteUrls.rejr}>
                    Reading Eggs Junior
                  </ProductLink>
                  <ProductLink href={siteUrls.rex}>
                    Reading Eggspress
                  </ProductLink>
                  <ProductLink href={siteUrls.maths}>Mathseeds</ProductLink>
                </ul>
              </nav>
            </div>

            <nav role="navigation" aria-label="Login" className="flex h-full">
              <ul className="flex items-center space-x-2">
                <li>
                  <LoginButton />
                </li>
                <li>
                  <SignupButton />
                </li>
              </ul>
            </nav>
          </>
        </Container>
      </div>

      <a href="/" className="block pt-[38px]">
        <div className="relative flex h-[95px] w-full justify-center bg-[#52b8f6] md:h-[160px]">
          <div className="hidden w-full max-w-[1542px] md:absolute md:block md:h-[160px]">
            <Image
              src={bgImage}
              alt="Background Image of a grassy field"
              className="object-cover"
              fill
              sizes="100vw"
            />
          </div>
          <Container className="relative flex h-[95px] items-center justify-center md:h-[160px] md:justify-start md:px-4">
            <>
              <div className="w-[97px] md:w-[146px]">
                <Image
                  src={logo}
                  alt={logoAltText}
                  sizes="100vw"
                  className="h-auto w-full"
                />
              </div>
              <div className="relative w-[63px] md:w-[95px]">
                <Image
                  src={reggie as ImageProps['src']}
                  alt="Reggie"
                  sizes="100vw"
                  className="h-auto w-full"
                />
              </div>
            </>
          </Container>
        </div>
      </a>
      <ReadingEggsNavBar />
    </header>
  )
}

interface SiteLocaleURLS {
  re: string
  rejr: string
  rex: string
  maths: string
}

function siteUrlsForLocale(locale: Locale): SiteLocaleURLS {
  let urls

  switch (locale) {
    case Locale.ca:
      urls = {
        re: 'https://readingeggs.ca/',
        rejr: 'https://readingeggsjunior.ca/',
        rex: 'https://readingeggspress.ca/',
        maths: 'https://mathseeds.ca/',
      }

      break
    case Locale.za:
      urls = {
        re: 'https://readingeggs.co.za/',
        rejr: 'https://readingeggsjunior.co.za/',
        rex: 'https://readingeggspress.co.za/',
        maths: 'https://mathseeds.co.za/',
      }

      break
    case Locale.us:
      urls = {
        re: 'https://readingeggs.com/',
        rejr: 'https://readingeggsjunior.com/',
        rex: 'https://readingeggspress.com/',
        maths: 'https://mathseeds.com/',
      }

      break
    case Locale.uk:
      urls = {
        re: 'https://readingeggs.co.uk/',
        rejr: 'https://readingeggsjunior.co.uk/',
        rex: 'https://readingeggspress.co.uk/',
        maths: 'https://mathseeds.co.uk/',
      }

      break
    case Locale.nz:
      urls = {
        re: 'https://readingeggs.co.nz/',
        rejr: 'https://readingeggsjunior.co.nz/',
        rex: 'https://readingeggspress.co.nz/',
        maths: 'https://mathseeds.co.nz/',
      }
      break
    default:
      urls = {
        re: 'https://readingeggs.com.au/',
        rejr: 'https://readingeggsjunior.com.au/',
        rex: 'https://readingeggspress.com.au/',
        maths: 'https://mathseeds.com.au/',
      }
  }
  return urls
}
